import type { FplActiveChip, LiveTeamPlayer } from './types';
import type { SubscribedMatchState } from './match-subscriber';
import {
  resolveEffectiveMultipliers,
  type PickAvailability
} from './auto-subs';
import { isFixtureSettled } from '@lib/live/match/status';
import type {
  LivePlayer,
  MatchState,
  MatchStatus
} from '@lib/live/match/types';

export type PickFixture = {
  matchState: MatchState;
  player: LivePlayer | null;
};

export const collectUniqueMatchIds = (picks: LiveTeamPlayer[]): string[] => {
  const ids = new Set<string>();
  for (const pick of picks) {
    for (const id of pick.optaMatchIds) ids.add(id);
  }
  return Array.from(ids);
};

/**
 * Opta player ids per match, for the squad only.
 *
 * Player channels are per player per match, and a match has around forty of
 * them. Subscribing to the whole roster would cost message volume for
 * thirty-odd players the pitch never draws, so each match subscriber is told
 * exactly which players to attach.
 *
 * A pick with no Opta id is left out. The response omits it for a player our
 * league records hold no row for, and there is no channel to name without one:
 * admitting the absent id would attach a channel that carries nothing, once
 * per fixture the pick features in. Those fixtures still get their main
 * channel, which is where the lineups and the clock come from.
 */
export const collectSquadOptaIdsByMatch = (
  picks: LiveTeamPlayer[]
): Record<string, string[]> => {
  const byMatch: Record<string, Set<string>> = {};
  for (const pick of picks) {
    if (!pick.optaId) continue;
    for (const matchId of pick.optaMatchIds) {
      byMatch[matchId] ??= new Set<string>();
      byMatch[matchId].add(pick.optaId);
    }
  }
  return Object.fromEntries(
    Object.entries(byMatch).map(([matchId, optaIds]) => [
      matchId,
      Array.from(optaIds).sort()
    ])
  );
};

/**
 * Find a pick's live state within a fixture.
 *
 * Keyed by Opta id first, because that is how both sources store it: player
 * channels are named by Opta id and the snapshot keys on the same value. The
 * pick carries its own `optaId`, so this needs nothing from the wire.
 *
 * The FPL id scan stays as a fallback rather than the primary lookup. A player
 * channel can deliver points before its `identity` slice has arrived, and
 * without identity there is no `fplId` to match, so scanning for it first would
 * read a channel that is actively delivering as an absent player, and absence
 * is what the availability rules treat as "left out of the squad".
 */
const findPickInState = (
  state: MatchState,
  // Absent for a pick the league data has no Opta id for; the FPL id scan
  // below already covers that case, so it costs nothing but the fast path.
  optaId: string | undefined,
  fplId: string
): LivePlayer | null => {
  const byOptaId = optaId === undefined ? undefined : state.players[optaId];
  if (byOptaId) return byOptaId;
  for (const candidate of Object.values(state.players)) {
    if (candidate.fplId === fplId) return candidate;
  }
  return null;
};

/**
 * Resolve every fixture for a pick that we have state for, joining
 * the pick's FPL element id against `LivePlayer.fplId` in each
 * fixture. Normal week: one entry. Double gameweek: one per fixture.
 * Blank gameweek: empty.
 */
export const findPickFixtures = (
  pick: LiveTeamPlayer,
  matchStates: Record<string, SubscribedMatchState>
): PickFixture[] => {
  const fplIdAsString = String(pick.id);
  const fixtures: PickFixture[] = [];
  for (const matchId of pick.optaMatchIds) {
    const subscribed = matchStates[matchId];
    if (!subscribed) continue;
    fixtures.push({
      matchState: subscribed.state,
      player: findPickInState(subscribed.state, pick.optaId, fplIdAsString)
    });
  }
  return fixtures;
};

const STATUS_PRIORITY: Partial<Record<MatchStatus, number>> = {
  LIVE: 100,
  SECOND_HALF: 100,
  HALF_TIME: 90,
  RECONCILING: 60,
  FULL_TIME: 40,
  LINEUPS_CONFIRMED: 30,
  SCHEDULED: 20,
  POSTPONED: 0,
  ABANDONED: 0
};

const getStatusPriority = (status: MatchStatus): number =>
  STATUS_PRIORITY[status] ?? 0;

const parseKickoffMs = (kickoffAt: string | null): number => {
  if (!kickoffAt) return 0;
  const parsed = Date.parse(kickoffAt);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * The fixture to surface when a pick has more than one. Priority:
 * live > HT > reconciling > finalised > pre-match. Within a tier,
 * prefer the latest kickoff. Returns null when the pick has no
 * fixture state subscribed yet.
 */
export const findPickActiveFixture = (
  pick: LiveTeamPlayer,
  matchStates: Record<string, SubscribedMatchState>
): PickFixture | null => {
  const fixtures = findPickFixtures(pick, matchStates);
  if (fixtures.length === 0) return null;
  return [...fixtures].sort((a, b) => {
    const priorityDelta =
      getStatusPriority(b.matchState.status) -
      getStatusPriority(a.matchState.status);
    if (priorityDelta !== 0) return priorityDelta;
    return (
      parseKickoffMs(b.matchState.kickoffAt) -
      parseKickoffMs(a.matchState.kickoffAt)
    );
  })[0];
};

/**
 * Sum of `player.points` across every fixture the pick has state for.
 * Double-gameweek aggregation matches how FPL totals a pick's gameweek
 * score. This is the BASE total, before captain or bench-boost
 * multipliers are applied.
 */
export const getPickTotalPoints = (
  pick: LiveTeamPlayer,
  matchStates: Record<string, SubscribedMatchState>
): number =>
  findPickFixtures(pick, matchStates).reduce(
    (sum, fixture) => sum + (fixture.player?.points ?? 0),
    0
  );

/**
 * Display-effective points for a pick — base × captaincy multiplier
 * for starters (so captain tiles show doubled, triple-captain tripled),
 * but raw base for bench picks. Their `multiplier` is 0; we still want
 * the tile to show what they scored even though it does not count
 * toward the team total without bench-boost.
 */
export const getPickEffectivePoints = (
  pick: LiveTeamPlayer,
  matchStates: Record<string, SubscribedMatchState>
): number => {
  const base = getPickTotalPoints(pick, matchStates);
  if (!pick.isStarter) return base;
  return base * pick.multiplier;
};

/** Active-fixture player for tile event icons. */
export const findPickPlayer = (
  pick: LiveTeamPlayer,
  matchStates: Record<string, SubscribedMatchState>
): LivePlayer | null =>
  findPickActiveFixture(pick, matchStates)?.player ?? null;

/** Active-fixture match state — currently unused by the pitch tile
 * but kept available for future surfaces (drawer fixture strip, etc). */
export const findPickMatchState = (
  pick: LiveTeamPlayer,
  matchStates: Record<string, SubscribedMatchState>
): MatchState | null =>
  findPickActiveFixture(pick, matchStates)?.matchState ?? null;

/**
 * Whether a pick has played, is confirmed not to be playing, or is still open.
 *
 * Two things settle a pick as `blank`. The first is parity with the existing
 * surfaces: its match finished and it recorded no minutes. The second is the
 * Opta signal. Once a team sheet is published the roster holds every player in
 * the matchday squad, so a pick missing from it has been left out and will not
 * play. That lands well before FPL's minutes data confirms it, which is what
 * lets the running score apply a substitution earlier than the old site can.
 *
 * Reading absence as a decision is only safe because of two guarantees in the
 * engine. Its roster is the starting XI plus the named substitutes, not just
 * the XI, so a bench player is present rather than missing. And it stages both
 * team sheets and publishes only once it holds both, so the roster is never
 * half a match with one side still to come.
 *
 * A double gameweek settles only when every fixture has: a pick with a match
 * still to come has not blanked, whatever the first fixture did.
 */
export const getPickAvailability = (
  pick: LiveTeamPlayer,
  matchStates: Record<string, SubscribedMatchState>
): PickAvailability => {
  // No fixture at all this gameweek, so there is nothing to wait for.
  if (pick.optaMatchIds.length === 0) return 'blank';

  const fplIdAsString = String(pick.id);
  let hasSettledEveryFixture = true;

  for (const matchId of pick.optaMatchIds) {
    const subscribed = matchStates[matchId];
    // Nothing known about this fixture yet, so nothing is decided.
    if (!subscribed?.hasMainSynced) {
      hasSettledEveryFixture = false;
      continue;
    }

    const player = findPickInState(
      subscribed.state,
      pick.optaId,
      fplIdAsString
    );
    if ((player?.minutesPlayed ?? 0) > 0) return 'played';

    const isSquadKnown = Object.keys(subscribed.state.players).length > 0;
    const isLeftOutOfSquad = isSquadKnown && player === null;
    const isFixtureOver = isFixtureSettled(
      subscribed.state.status,
      subscribed.state.isFinalised
    );
    if (!isLeftOutOfSquad && !isFixtureOver) hasSettledEveryFixture = false;
  }

  return hasSettledEveryFixture ? 'blank' : 'pending';
};

/** Availability for every pick, keyed by the slot the auto-sub rules use. */
export const collectAvailability = (
  picks: LiveTeamPlayer[],
  matchStates: Record<string, SubscribedMatchState>
): Map<number, PickAvailability> =>
  new Map(
    picks.map((pick) => [pick.slot, getPickAvailability(pick, matchStates)])
  );

/**
 * The running live score, with auto-subs and the vice-captaincy applied.
 *
 * Without them this diverges from every other FFH surface the moment a starter
 * blanks: bench points stay stranded at a zero multiplier and a captain who
 * never took the pitch keeps the armband.
 */
export const calculateLiveTotal = (
  picks: LiveTeamPlayer[],
  matchStates: Record<string, SubscribedMatchState>,
  activeChip: FplActiveChip
): number => {
  const { multipliers } = resolveEffectiveMultipliers(
    picks,
    collectAvailability(picks, matchStates),
    activeChip
  );

  return picks.reduce(
    (sum, pick) =>
      sum +
      getPickTotalPoints(pick, matchStates) * (multipliers.get(pick.slot) ?? 0),
    0
  );
};

export const isAnyChannelLive = (
  matchStates: Record<string, SubscribedMatchState>
): boolean => Object.values(matchStates).some((entry) => entry.isLive);

/**
 * Whether a pick's points come from a simulated fixture.
 *
 * Every fixture is considered, not just the active one. A double gameweek sums
 * a pick's points across both, so a pick with one real fixture and one
 * simulated fixture is showing a total that is partly fabricated, and leaving
 * it unmarked because the active fixture happened to be the real one would be
 * a lie by omission.
 */
export const isPickSimulated = (
  pick: LiveTeamPlayer,
  matchStates: Record<string, SubscribedMatchState>
): boolean =>
  findPickFixtures(pick, matchStates).some(
    (fixture) => fixture.matchState.provenance !== null
  );

/**
 * Distinct provenance identifiers across every subscribed fixture.
 *
 * Sorted so the rendered order does not depend on the order fixtures happened
 * to subscribe in.
 */
export const collectSimulatedProvenances = (
  matchStates: Record<string, SubscribedMatchState>
): string[] =>
  Array.from(
    new Set(
      Object.values(matchStates)
        .map((entry) => entry.state.provenance)
        .filter((provenance): provenance is string => provenance !== null)
    )
  ).sort();

export type SimulatedPickCount = {
  /** Picks with at least one simulated fixture. */
  simulated: number;
  /** Picks we hold any fixture state for, simulated or not. */
  withFixture: number;
};

/**
 * How much of the pitch is fabricated.
 *
 * Counted against picks that have fixture state rather than all fifteen,
 * because a blank-gameweek pick contributes nothing either way and including
 * it would understate how much of the visible total is made up.
 */
export const countSimulatedPicks = (
  picks: LiveTeamPlayer[],
  matchStates: Record<string, SubscribedMatchState>
): SimulatedPickCount => {
  let simulated = 0;
  let withFixture = 0;

  for (const pick of picks) {
    if (findPickFixtures(pick, matchStates).length === 0) continue;
    withFixture += 1;
    if (isPickSimulated(pick, matchStates)) simulated += 1;
  }

  return { simulated, withFixture };
};
